/**
 * ATTIFIT — scoped "+ Add" quick-add popup.
 *
 * Opens a compact variant-choice dialog for product cards in the two
 * approved contexts (PDP related-products, cart drawer cross-sells; the
 * pills are rendered by snippets/attifit-quick-add-button.liquid, only
 * when product-block.liquid receives attifit_quick_add: true).
 *
 * How it works on Symmetry 8.3.2 (see ATTIFIT-CUSTOMIZATIONS.md):
 * - Content comes from fetching the product's own (localized) page URL -
 *   the same technique as Symmetry's native quick buy - reduced to
 *   .section-main-product .quickbuy-content, so "Show in quick buy" block
 *   settings keep working. A JSON-template section can only be requested
 *   by its DYNAMIC runtime id (template--...__main), which is unknowable
 *   before a first render, so the full page is the only reliable initial
 *   source; the fetched variant-picker carries that dynamic id in
 *   data-section-id, which keeps every subsequent native `sections=`
 *   variant request valid.
 * - The popup body carries .js-quickbuy, so the fetched variant-picker runs
 *   in its native quick-buy mode (section-scoped fetches, no URL updates).
 *   Option changes therefore go through the theme's own variant engine;
 *   this file never resolves variants itself.
 * - 8.3.2 ids are section-scoped (product-form-template--...__main-...),
 *   so every fetched product collides with a host PDP. Each open gets a
 *   unique instance suffix applied to the initial content AND to every
 *   variant-response fragment (via the theme's
 *   on:variant:before-replace-element hook).
 * - Add to cart is the native ProductForm inside the fetched content: one
 *   on:cart:add, native section merge, native drawer opening (the
 *   already-open case is guarded in assets/cart-drawer.js).
 *
 * Pending-variant lock: the native picker resolves every option change on
 * the server, so between "option clicked" and "response applied" the UI can
 * show a selection whose variant id is not in the form yet. On every change
 * inside the popup the submit is disabled and a capture-phase submit guard
 * blocks the form; it unlocks only when the picker announces a completed
 * request AND the visible selections, the embedded variant JSON and the
 * hidden id input all agree. Failed requests keep it locked and show a
 * recoverable error (see the on:variant:error dispatch in
 * assets/variant-picker.js). No timers ever unlock anything.
 */
(function () {
  'use strict';

  if (window.attifitQuickAddInit) return;
  window.attifitQuickAddInit = true;

  var modal = document.getElementById('AttifitQuickAdd');
  if (!modal) return;

  var panel = modal.querySelector('.attifit-qa-modal__panel');
  var bodyEl = modal.querySelector('.attifit-qa-modal__body');
  var statusEl = modal.querySelector('.attifit-qa-modal__status');
  var overlay = modal.querySelector('.attifit-qa-modal__overlay');
  var closeBtn = modal.querySelector('.attifit-qa-modal__close');

  var KNOWN_ASSETS = {
    'variant-picker': modal.dataset.assetVariantPicker,
    'custom-select': modal.dataset.assetCustomSelect,
    'open-or-scroll-to': modal.dataset.assetOpenOrScrollTo
  };

  var openToken = 0;
  var instanceSuffix = '';
  var lastTrigger = null;
  var originDrawer = null;
  var fetchAbort = null;
  var variantPending = false;
  var assetPromises = {}; // tag -> in-flight/successful script-load Promise (failures evicted)
  var sectionCache = new Map(); // url -> Promise resolving to page HTML (successes only)
  // ONE ATTIFIT quick-add cart mutation (direct OR popup) at a time.
  // { token, mode: 'direct'|'popup', productForm, productId, trigger,
  //   drawerOrigin, popupInstance }
  var quickAddMutation = null;
  var quickAddMutationToken = 0;

  /* ------------------------------------------------------------------ *
   * Utilities
   * ------------------------------------------------------------------ */

  // Root-AND-descendant id suffixing. Self-contained (not built on
  // theme.suffixIds, which walks descendants only and appends to IDREF
  // lists as one raw string): single-IDREF attributes get the suffix
  // appended, IDREF-LIST attributes (aria-labelledby/-describedby/
  // -controls) get every space-separated token suffixed individually.
  // Applies each suffix at most once per tree.
  var SUFFIX_ATTRS_SINGLE = ['id', 'for', 'form', 'aria-activedescendant', 'data-target-id', 'data-modal'];
  var SUFFIX_ATTRS_LIST = ['aria-labelledby', 'aria-describedby', 'aria-controls'];
  var SUFFIX_SELECTOR = SUFFIX_ATTRS_SINGLE.concat(SUFFIX_ATTRS_LIST).map(function (attr) {
    return '[' + attr + ']';
  }).join(',');

  function suffixElementIds(el, suffix) {
    SUFFIX_ATTRS_SINGLE.forEach(function (attr) {
      if (el.hasAttribute(attr)) el.setAttribute(attr, el.getAttribute(attr) + suffix);
    });
    SUFFIX_ATTRS_LIST.forEach(function (attr) {
      if (!el.hasAttribute(attr)) return;
      var tokens = el.getAttribute(attr).trim().split(/\s+/).filter(function (t) { return t.length; });
      if (!tokens.length) return;
      el.setAttribute(attr, tokens.map(function (t) { return t + suffix; }).join(' '));
    });
  }

  function attifitSuffixIds(root, suffix) {
    if (!root || root.nodeType !== 1) return;
    if (root.dataset.attifitSuffixed === suffix) return;
    suffixElementIds(root, suffix);
    root.querySelectorAll(SUFFIX_SELECTOR).forEach(function (el) { suffixElementIds(el, suffix); });
    root.dataset.attifitSuffixed = suffix;
  }

  function getPicker() {
    return bodyEl.querySelector('variant-picker');
  }

  function getAddButton() {
    return bodyEl.querySelector('.js-product-form [name="add"]');
  }

  function getIdInput() {
    return bodyEl.querySelector('.js-product-form input[name="id"]');
  }

  function setStatus(text) {
    statusEl.textContent = text || '';
  }

  function showErrorState(productUrl, keepContent) {
    if (!keepContent) bodyEl.innerHTML = '';
    statusEl.textContent = '';
    var msg = document.createElement('span');
    msg.textContent = modal.dataset.textError + ' ';
    var link = document.createElement('a');
    link.className = 'attifit-qa-modal__error-link underline';
    link.href = productUrl;
    link.textContent = modal.dataset.textViewDetails;
    statusEl.appendChild(msg);
    statusEl.appendChild(link);
  }

  /* ------------------------------------------------------------------ *
   * Controlled asset loading (no scanning of fetched HTML for scripts)
   * ------------------------------------------------------------------ */

  // Promise-per-tag loader: resolves on script load (whenDefined gates
  // actual readiness separately), rejects on script error and evicts its
  // cached entry so the NEXT popup open retries - a transient network
  // failure must not disable quick add for the rest of the session.
  // Successful and in-flight loads are deduplicated.
  function loadAssetFor(tag) {
    if (customElements.get(tag)) return Promise.resolve();
    if (assetPromises[tag]) return assetPromises[tag];
    var src = KNOWN_ASSETS[tag];
    if (!src) return Promise.resolve(); // not ours to load; whenDefined decides readiness
    var promise = new Promise(function (resolve, reject) {
      if (document.querySelector('script[src="' + src + '"]')) {
        resolve(); // a section's own tag is already loading it
        return;
      }
      var scr = document.createElement('script');
      scr.src = src;
      scr.defer = true;
      scr.addEventListener('load', function () { resolve(); });
      scr.addEventListener('error', function () {
        delete assetPromises[tag];
        scr.remove();
        reject(new Error('Failed to load ' + src));
      });
      document.head.appendChild(scr);
    });
    assetPromises[tag] = promise;
    return promise;
  }

  // Resolves when all tags are defined; rejects after ms so a missing asset
  // becomes a visible error with a product-page link - the timeout can only
  // ever produce the error state, never an interactive form.
  function waitForDefined(tags, ms) {
    if (!tags.length) return Promise.resolve();
    return new Promise(function (resolve, reject) {
      var timer = setTimeout(function () { reject(new Error('Timed out waiting for: ' + tags.join(', '))); }, ms);
      Promise.all(tags.map(function (tag) { return customElements.whenDefined(tag); }))
        .then(function () { clearTimeout(timer); resolve(); }, function (err) { clearTimeout(timer); reject(err); });
    });
  }

  /* ------------------------------------------------------------------ *
   * Product page fetch (session cache of successes only)
   * ------------------------------------------------------------------ */

  var PAGE_FETCH_TIMEOUT_MS = 15000;
  var MAX_PAGE_CACHE = 10;

  // Tie a consumer to its own open's abort signal without aborting the
  // shared underlying request (a close must not poison the cache entry the
  // next open reuses).
  function withConsumerAbort(request, signal) {
    if (!signal) return request;
    return new Promise(function (resolve, reject) {
      var onAbort = function () { reject(new DOMException('Aborted', 'AbortError')); };
      if (signal.aborted) { onAbort(); return; }
      signal.addEventListener('abort', onAbort, { once: true });
      request.then(function (text) {
        signal.removeEventListener('abort', onAbort);
        resolve(text);
      }, function (err) {
        signal.removeEventListener('abort', onAbort);
        reject(err);
      });
    });
  }

  function fetchProductPage(url, signal) {
    if (sectionCache.has(url)) return withConsumerAbort(sectionCache.get(url), signal);

    // The cached promise resolves only after SEMANTIC validation - an
    // HTTP-200 page without the main-product quick-buy content (challenge
    // pages, transient storefront errors) must never be cached, or the
    // product would stay broken for the whole session. The shared request
    // also gets its own internal timeout: consumer signals reject only
    // their caller, so without this a stalled fetch would sit in the cache
    // pending forever.
    var internalAbort = 'AbortController' in window ? new AbortController() : null;
    var timedOut = false;
    var timeoutId = internalAbort
      ? setTimeout(function () { timedOut = true; internalAbort.abort(); }, PAGE_FETCH_TIMEOUT_MS)
      : null;

    var request = fetch(url, internalAbort ? { signal: internalAbort.signal } : undefined)
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.text();
      })
      .then(function (html) {
        var probe = document.createElement('template');
        probe.innerHTML = html;
        if (!probe.content.querySelector('.section-main-product .quickbuy-content')) {
          throw new Error('No main-product .quickbuy-content in page response');
        }
        return html;
      })
      .catch(function (err) {
        if (timedOut) throw new Error('Timed out loading the product page');
        throw err;
      })
      .finally(function () {
        if (timeoutId) clearTimeout(timeoutId);
      });

    // Never cache failures - but only evict OUR OWN entry: a cap-evicted or
    // timed-out old request settling late must not delete a newer request
    // already cached for the same URL.
    request.catch(function () {
      if (sectionCache.get(url) === request) sectionCache.delete(url);
    });

    if (sectionCache.size >= MAX_PAGE_CACHE) {
      var oldest = sectionCache.keys().next();
      if (!oldest.done) sectionCache.delete(oldest.value);
    }
    sectionCache.set(url, request);
    return withConsumerAbort(request, signal);
  }

  // Prefetch on genuine hover intent only (a full product page is heavy):
  // the pointer must rest on the card for PREFETCH_INTENT_MS before the
  // request starts - long enough that sweeping the pointer across a grid
  // fetches nothing. No touch prefetch: touches during scrolling would
  // fetch whole pages.
  var PREFETCH_INTENT_MS = 400;
  var prefetchTimers = new Map();

  function schedulePrefetch(evt) {
    if (!evt.target.closest) return;
    var conn = navigator.connection;
    if (conn && (conn.saveData || /(^|-)2g$/.test(conn.effectiveType || ''))) return;
    var block = evt.target.closest('.product-block');
    if (!block || prefetchTimers.has(block)) return;
    var trigger = block.querySelector('[data-attifit-qa="popup"][data-product-url]');
    if (!trigger) return;
    var url = trigger.dataset.productUrl;
    prefetchTimers.set(block, setTimeout(function () {
      prefetchTimers.delete(block);
      fetchProductPage(url).catch(function () {});
    }, PREFETCH_INTENT_MS));
  }

  function cancelPrefetch(evt) {
    if (!evt.target.closest) return;
    var block = evt.target.closest('.product-block');
    if (!block || !prefetchTimers.has(block)) return;
    if (evt.relatedTarget && block.contains(evt.relatedTarget)) return; // still inside the card
    clearTimeout(prefetchTimers.get(block));
    prefetchTimers.delete(block);
  }

  /* ------------------------------------------------------------------ *
   * Content preparation
   * ------------------------------------------------------------------ */

  // Everything the popup must never contain: what the native quick buy
  // hides, plus the media gallery (a header thumbnail stands in for it),
  // payment buttons, pickup availability (element, wrapper AND the hidden
  // .js-pickup-drawer side drawer that product-popups renders separately),
  // instalments and structured data. Applied to the initial content and to
  // every variant-response fragment, so a replaced "-modals" block cannot
  // re-introduce any of it. The embedded variant JSON
  // (type="application/json") must survive.
  var STRIP_SELECTORS = [
    '.media-gallery',
    '.product-media-collage',
    '.not-in-quickbuy',
    'pickup-availability',
    '[data-dynamic-variant-content$="-pickup-availability"]',
    '.js-pickup-drawer',
    'buy-buttons',
    '.product-dynamic-payment-button',
    '.js-instalments-form',
    '.sticky-atc-anchor',
    '.skip-link',
    'noscript',
    'script[src]',
    'script[type="application/ld+json"]'
  ].join(',');

  function stripPopupContent(root) {
    root.querySelectorAll(STRIP_SELECTORS).forEach(function (el) { el.remove(); });
  }

  function prepareContent(content, trigger, productUrl) {
    stripPopupContent(content);

    content.querySelectorAll('.only-in-quickbuy').forEach(function (el) { el.classList.remove('only-in-quickbuy'); });
    content.querySelectorAll('.sticky-column-desktop').forEach(function (el) { el.classList.remove('sticky-column-desktop'); });
    content.querySelectorAll('variant-picker[data-update-url="true"]').forEach(function (el) { el.dataset.updateUrl = 'false'; });

    // Scroll-in animation attributes would keep injected content invisible
    // (nothing observes it in the popup). Same strip the native cart merge
    // performs in CartForm.refreshFromHtml.
    stripAnimationAttrs(content);

    linkifyTitles(content, productUrl);

    // Compact header: card image beside title + price.
    var head = document.createElement('div');
    head.className = 'attifit-qa-head';
    if (trigger && trigger.dataset.image) {
      var img = document.createElement('img');
      img.className = 'attifit-qa-head__image';
      img.src = trigger.dataset.image;
      img.alt = '';
      head.appendChild(img);
    }
    var text = document.createElement('div');
    text.className = 'attifit-qa-head__text';
    var titleRow = content.querySelector('.title-row');
    var priceRow = content.querySelector('.price-container');
    if (titleRow) text.appendChild(titleRow);
    if (priceRow) text.appendChild(priceRow);
    head.appendChild(text);
    content.insertBefore(head, content.firstChild);

    attifitSuffixIds(content, instanceSuffix);
    return content;
  }

  function linkifyTitles(root, productUrl) {
    root.querySelectorAll('.product-title').forEach(function (el) {
      if (el.querySelector('a')) return;
      var link = document.createElement('a');
      link.href = productUrl;
      while (el.firstChild) link.appendChild(el.firstChild);
      el.appendChild(link);
    });
  }

  function stripAnimationAttrs(root) {
    if (root.removeAttribute) {
      root.removeAttribute('data-cc-animate');
      root.removeAttribute('data-cc-animate-delay');
    }
    root.querySelectorAll('[data-cc-animate]').forEach(function (el) {
      el.removeAttribute('data-cc-animate');
      el.removeAttribute('data-cc-animate-delay');
    });
  }

  /* ------------------------------------------------------------------ *
   * Pending-variant lock (see header comment)
   * ------------------------------------------------------------------ */

  function setVariantPending() {
    variantPending = true;
    modal.setAttribute('data-attifit-pending', '');
    setStatus('');
    var btn = getAddButton();
    if (btn) {
      btn.disabled = true;
      btn.setAttribute('aria-disabled', 'true');
    }
  }

  function clearVariantPending() {
    variantPending = false;
    modal.removeAttribute('data-attifit-pending');
    var picker = getPicker();
    var btn = getAddButton();
    if (btn) btn.removeAttribute('aria-disabled');
    // Native code decides the button's real state (sold out, no variant...).
    if (picker && typeof picker.updateAddToCartButton === 'function') {
      picker.updateAddToCartButton();
    }
  }

  function optionsMatch(variantOptions, selectedOptions) {
    if (!variantOptions || variantOptions.length !== selectedOptions.length) return false;
    for (var i = 0; i < variantOptions.length; i += 1) {
      if (String(variantOptions[i]) !== String(selectedOptions[i])) return false;
    }
    return true;
  }

  // Unlock only when the visible selections, the embedded variant JSON and
  // the hidden id input all describe the same state. Called on every
  // on:variant:change from inside the popup; a mismatch means a newer
  // request is still pending, so stay locked and wait for its announce.
  function trySettleVariantState() {
    if (!variantPending) return;
    // While a popup cart mutation is in flight nothing may visually
    // re-enable the form (the submitted state is frozen); the mutation's
    // own settle re-runs this once released.
    if (quickAddMutation && quickAddMutation.mode === 'popup') return;
    var picker = getPicker();
    var idInput = getIdInput();
    if (!picker || !idInput || typeof picker.getSelectedOptions !== 'function') return;

    var selections = picker.getSelectedOptions();
    if (selections.indexOf(null) !== -1) {
      // Valid resting state before all options are chosen: the hidden id is
      // empty (the theme only fills it on a full selection) and the native
      // required-option validation guards submission.
      if (idInput.value === '') clearVariantPending();
      return;
    }

    var variantData = typeof picker.getVariantData === 'function' ? picker.getVariantData() : null;
    if (!variantData) return;
    if (String(variantData.id) !== String(idInput.value)) return;
    if (!optionsMatch(variantData.options, selections)) return;
    clearVariantPending();
  }

  // Every option change inside the popup locks the form immediately.
  bodyEl.addEventListener('change', function (evt) {
    if (modal.hidden) return;
    if (!evt.target.closest || !evt.target.closest('variant-picker')) return;
    setVariantPending();
  });

  // Completed (non-stale) picker request: try to settle.
  bodyEl.addEventListener('on:variant:change', function (evt) {
    if (modal.hidden) return;
    // Keep the header thumbnail in sync with the selected variant.
    var variant = evt.detail && evt.detail.variant;
    var img = bodyEl.querySelector('.attifit-qa-head__image') || panel.querySelector('.attifit-qa-head__image');
    if (img && variant && variant.featured_media && variant.featured_media.preview_image && variant.featured_media.preview_image.src) {
      var src = variant.featured_media.preview_image.src;
      img.src = src + (src.indexOf('?') > -1 ? '&' : '?') + 'width=360';
    }
    trySettleVariantState();
  });

  // Failed picker request (dispatched by the ATTIFIT catch in
  // assets/variant-picker.js): stay locked, offer recovery.
  document.addEventListener('on:variant:error', function (evt) {
    if (modal.hidden || !bodyEl.contains(evt.target)) return;
    showErrorState(bodyEl.dataset.productUrl || '#', true);
  });

  // Hard gate: while a variant request is unsettled OR any ATTIFIT
  // quick-add mutation is already running, nothing in the popup submits -
  // capture phase, so this runs before the native ProductForm handler.
  modal.addEventListener('submit', function (evt) {
    if (variantPending || quickAddMutation) {
      evt.preventDefault();
      evt.stopPropagation();
    }
  }, true);

  // Trusted-interaction fallback for the submitted-state freeze: option
  // and quantity correctness must not rest on inert/CSS alone. Everything
  // carrying the frozen marker rejects real user input at capture, before
  // any native handler runs; Tab (navigation) and Escape (dismissal) stay
  // live, and programmatic events pass so native internals keep working.
  ['click', 'keydown', 'beforeinput'].forEach(function (type) {
    modal.addEventListener(type, function (evt) {
      if (!evt.isTrusted) return;
      if (!quickAddMutation || quickAddMutation.mode !== 'popup') return;
      if (type === 'keydown' && (evt.key === 'Tab' || evt.key === 'Escape')) return;
      if (!evt.target.closest || !evt.target.closest('[data-attifit-qa-frozen]')) return;
      evt.preventDefault();
      evt.stopPropagation();
    }, true);
  });

  // Every variant-response fragment gets this instance's suffix BEFORE the
  // theme reads ids for value transfer or inserts it (the native
  // convertToQuickBuyContent that would do this is absent while the global
  // quick buy is off). Also keeps fragments consistent with the initial
  // transform: no URL updates, title stays linked, and the submit arrives
  // pre-disabled while a request is pending.
  document.addEventListener('on:variant:before-replace-element', function (evt) {
    var detail = evt.detail || {};
    if (!detail.toReplace || !detail.replaceWith || !bodyEl.contains(detail.toReplace)) return;
    var fragment = detail.replaceWith;
    attifitSuffixIds(fragment, instanceSuffix);
    if (fragment.querySelectorAll) {
      stripPopupContent(fragment);
      fragment.querySelectorAll('variant-picker[data-update-url="true"]').forEach(function (el) { el.dataset.updateUrl = 'false'; });
      stripAnimationAttrs(fragment);
      linkifyTitles(fragment, bodyEl.dataset.productUrl || '#');
      var mutationHold = quickAddMutation && quickAddMutation.mode === 'popup';
      if (variantPending || mutationHold) {
        fragment.querySelectorAll('[name="add"]').forEach(function (btn) {
          btn.disabled = true;
          btn.setAttribute('aria-disabled', 'true');
        });
      }
      if (mutationHold) {
        // Trusted changes are frozen while a popup cart request runs, so a
        // fragment can only arrive here through a programmatic change -
        // whatever swaps in inherits the freeze and can never look usable
        // until the request settles (unfreeze restores or the close
        // discards it).
        if (fragment.matches && fragment.matches(FREEZE_REGION_SELECTOR)) {
          freezeRegion(quickAddMutation, fragment);
        }
        fragment.querySelectorAll(FREEZE_REGION_SELECTOR).forEach(function (el) {
          freezeRegion(quickAddMutation, el);
        });
      }
    }
  });

  /* ------------------------------------------------------------------ *
   * Open / close / focus
   * ------------------------------------------------------------------ */

  function openPopup(trigger) {
    lastTrigger = trigger;
    originDrawer = trigger.closest('.js-cart-drawer');
    openToken += 1;
    var token = openToken;
    instanceSuffix = '-attifit-qa-' + token;
    // Ownership stamp for the persistent body: the fetched variant-picker
    // captures this value per request and refuses to touch the container
    // once it changes (see the ATTIFIT guards in assets/variant-picker.js).
    bodyEl.dataset.attifitInstance = String(token);
    if (fetchAbort) fetchAbort.abort();
    fetchAbort = 'AbortController' in window ? new AbortController() : null;

    variantPending = false;
    modal.removeAttribute('data-attifit-pending');
    modal.hidden = false;
    document.documentElement.classList.add('attifit-qa-lock');

    var title = trigger.dataset.productTitle || '';
    if (!panel.dataset.baseLabel) panel.dataset.baseLabel = panel.getAttribute('aria-label') || '';
    panel.setAttribute('aria-label', panel.dataset.baseLabel + (title ? ': ' + title : ''));

    var productUrl = trigger.dataset.productUrl;
    bodyEl.dataset.productUrl = productUrl;
    bodyEl.innerHTML = '<div class="loading-spinner"></div>';
    setStatus(modal.dataset.textLoading);
    panel.focus({ preventScroll: true });

    fetchProductPage(productUrl, fetchAbort ? fetchAbort.signal : undefined)
      .then(function (html) {
        if (token !== openToken || modal.hidden) return;

        var tpl = document.createElement('template');
        tpl.innerHTML = html;
        // Scoped to the main product section wrapper (its schema class), so
        // no other section's markup can ever be picked up.
        var source = tpl.content.querySelector('.section-main-product .quickbuy-content');
        if (!source) throw new Error('No main-product .quickbuy-content in page response');

        var content = prepareContent(source.cloneNode(true), trigger, productUrl);

        // Wait for the custom elements this content actually uses; load the
        // known assets for any that are missing (home/collection pages
        // never load the per-section picker scripts). A load failure
        // rejects into the error state and is retryable on the next open.
        var needed = Object.keys(KNOWN_ASSETS).filter(function (tag) {
          return content.querySelector(tag) && !customElements.get(tag);
        });

        return Promise.all(needed.map(loadAssetFor)).then(function () {
          return waitForDefined(needed, 5000);
        }).catch(function (err) {
          // A definition timeout with the element still undefined means the
          // load failed silently - including a SECTION-supplied script tag
          // whose earlier failure loadAssetFor could not observe (it
          // resolves immediately for pre-existing tags). Evict our promise
          // and the dead tag so the next open genuinely retries; a
          // successfully defined element is never touched, so no duplicate
          // successful requests occur.
          needed.forEach(function (tag) {
            if (customElements.get(tag)) return;
            delete assetPromises[tag];
            var src = KNOWN_ASSETS[tag];
            if (src) {
              var stale = document.querySelector('script[src="' + src + '"]');
              if (stale) stale.remove();
            }
          });
          throw err;
        }).then(function () {
          if (token !== openToken || modal.hidden) return;
          setStatus('');
          bodyEl.innerHTML = '';
          bodyEl.appendChild(content);
        });
      })
      .catch(function (error) {
        if (token !== openToken || modal.hidden) return;
        if (error && error.name === 'AbortError') return;
        console.warn('[attifit-quick-add]', error); // eslint-disable-line no-console
        showErrorState(productUrl, false);
      });
  }

  function focusIntoDrawer(trigger) {
    var drawer = document.querySelector('.js-cart-drawer[open]');
    if (!drawer) return false;
    var target = null;
    if (trigger && trigger.isConnected && drawer.contains(trigger)) {
      target = trigger;
    } else if (trigger) {
      // The cart merge may have re-rendered the cross-sells: find the same
      // product's fresh pill.
      var block = trigger.dataset && trigger.dataset.productUrl
        ? drawer.querySelector('.product-block [data-attifit-qa="popup"][data-product-url="' + trigger.dataset.productUrl + '"]')
        : null;
      if (block) target = block;
    }
    if (!target) target = drawer.querySelector('.drawer__close-btn') || drawer;
    // Re-arm the drawer's focus trap rather than just focusing: a native
    // modal opened from inside the popup (the size chart) calls trapFocus
    // for itself, which removes the drawer's single-instance trap entirely.
    // trapFocus focuses the target itself, and the drawer's own close still
    // restores its remembered opener afterwards. Every candidate target is
    // inside the drawer.
    if (typeof window.trapFocus === 'function') {
      window.trapFocus(drawer, target);
    } else {
      target.focus({ preventScroll: true });
    }
    return true;
  }

  // restoreFocus: true (default) -> the trigger (or drawer fallback),
  // 'drawer' -> a valid element inside the open drawer,
  // false -> leave focus where it is (drawer already holds it).
  function closePopup(restoreFocus) {
    if (modal.hidden) return;
    openToken += 1; // invalidate any in-flight open
    if (fetchAbort) fetchAbort.abort();
    fetchAbort = null;

    modal.hidden = true;
    document.documentElement.classList.remove('attifit-qa-lock');
    // Drop the ownership stamp BEFORE clearing content: any in-flight
    // variant request captured the old value and now fails its ownership
    // check instead of mutating an emptied (or soon reused) body.
    delete bodyEl.dataset.attifitInstance;
    bodyEl.innerHTML = '';
    bodyEl.removeAttribute('data-product-url');
    setStatus('');
    variantPending = false;
    modal.removeAttribute('data-attifit-pending');

    if (restoreFocus !== false) {
      // Any drawer-origin close (Escape, overlay, close button, add) must
      // go through focusIntoDrawer(), which re-arms the drawer's focus
      // trap - not a bare trigger.focus(). A native modal opened from
      // inside the popup (the size chart) removes the theme's single
      // global trap when it traps itself, so the drawer's containment may
      // already be gone by close time even when the trigger still exists.
      var restored = false;
      if (restoreFocus === 'drawer' || originDrawer) {
        restored = focusIntoDrawer(lastTrigger);
      }
      if (!restored) {
        if (lastTrigger && lastTrigger.isConnected) {
          lastTrigger.focus({ preventScroll: true });
        } else {
          focusIntoDrawer(lastTrigger);
        }
      }
    }

    lastTrigger = null;
    originDrawer = null;
  }

  // Symmetry's cart drawer closes on Escape KEYUP bound to itself. Closing
  // the popup on keydown can put focus back inside the drawer before the
  // very same press's keyup fires there - swallow exactly that one keyup
  // (capture phase, Escape-matched, bounded cleanup). First press closes
  // the popup, the next closes the drawer. (Tested pattern from the Bluvap
  // reference implementation.)
  function swallowEscapeKeyup() {
    var timer = null;
    function stop() {
      clearTimeout(timer);
      document.removeEventListener('keyup', onKeyup, true);
    }
    function onKeyup(evt) {
      if (evt.key !== 'Escape') return; // a released modifier must not retire this
      stop();
      evt.stopPropagation();
    }
    timer = setTimeout(stop, 2000);
    document.addEventListener('keyup', onKeyup, true);
  }

  document.addEventListener('keydown', function (evt) {
    if (modal.hidden) return;

    // A native modal opened from inside the popup (the size chart) owns the
    // press: it closes itself on keyup, and its trapFocus owns Tab. Yield
    // both keys while one is open.
    var nativeModalOpen = panel.querySelector('modal-dialog[open]');

    if (evt.key === 'Escape') {
      if (nativeModalOpen || (evt.target.closest && evt.target.closest('modal-dialog'))) return;
      closePopup();
      swallowEscapeKeyup();
      return;
    }

    if (evt.key === 'Tab' && !nativeModalOpen) {
      var focusables = Array.prototype.filter.call(
        panel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'),
        function (el) { return el.offsetParent !== null; }
      );
      if (!focusables.length) return;
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      var active = document.activeElement;
      if (evt.shiftKey && (active === first || active === panel || !panel.contains(active))) {
        evt.preventDefault();
        last.focus();
      } else if (!evt.shiftKey && (active === last || !panel.contains(active))) {
        evt.preventDefault();
        first.focus();
      }
    }
  });

  overlay.addEventListener('click', function () { closePopup(); });
  closeBtn.addEventListener('click', function () { closePopup(); });

  // Popup success/close handling lives in finishPopupMutation(), invoked
  // from the request-specific listeners that beginQuickAddMutation attaches
  // to the submitting <product-form>. A document-level on:cart:add listener
  // cannot be authoritative here: after an early manual close the detached
  // form's events never bubble to document, yet the cart request is still
  // running and must be owned to completion.

  /* ------------------------------------------------------------------ *
   * Unified quick-add cart-mutation lifecycle
   * ------------------------------------------------------------------ *
   * ONE ATTIFIT quick-add cart mutation runs at a time - direct mini-form
   * OR popup form (the original requirement: direct add and popup add must
   * never fire together, or two cart-section snapshots can apply out of
   * order). The lock is REQUEST-SPECIFIC: native ProductForm dispatches
   * on:cart:add / on:cart:error / on:cart:change on the <product-form>
   * element itself, and listeners attached directly to that element still
   * fire after it is detached - the drawer form removed by the cross-sell
   * merge AND the popup form discarded by an early popup close both still
   * report completion. Document-level cart events (a quantity update's
   * on:cart:change, an unrelated merge) never pass through the submitting
   * form and cannot unlock anything. The lock deliberately does NOT touch
   * the main PDP product form or any other theme form.
   */
  function holdQuickAddControls(submittingForm) {
    document.documentElement.setAttribute('data-attifit-qa-busy', '');
    document.querySelectorAll('.attifit-qa-direct [name="add"], [data-attifit-qa="popup"]').forEach(function (el) {
      // Never touch the submitting control: native ProductForm must receive
      // its submit event and manage its own aria-disabled/is-loading.
      if (submittingForm && submittingForm.contains(el)) return;
      // The marker value records whether the aria-disabled belongs to
      // ATTIFIT ('added') or predated the hold ('kept'): release removes
      // only what ATTIFIT introduced.
      if (el.getAttribute('aria-disabled') === 'true') {
        el.setAttribute('data-attifit-qa-held', 'kept');
      } else {
        el.setAttribute('aria-disabled', 'true');
        el.setAttribute('data-attifit-qa-held', 'added');
      }
    });
  }

  function releaseQuickAddControls() {
    document.documentElement.removeAttribute('data-attifit-qa-busy');
    document.querySelectorAll('[data-attifit-qa-held]').forEach(function (el) {
      if (el.getAttribute('data-attifit-qa-held') === 'added') el.removeAttribute('aria-disabled');
      el.removeAttribute('data-attifit-qa-held');
    });
  }

  /* Submitted-state freeze. Native ProductForm builds its FormData
   * synchronously inside its own submit handler - BEFORE the bubble
   * listener below can begin a popup mutation - so the request always
   * carries the variant id and quantity that were visible at the moment
   * Add was pressed. Every control that could make the VISIBLE state
   * drift away from that captured state while the request runs (option
   * inputs, dropdown custom-selects, the quantity stepper) is therefore
   * frozen until the request settles: on a slow network a shopper who
   * switched to White/L mid-request would otherwise watch the popup close
   * on a successful add of Black/M. inert is the primary mechanism
   * (pointer, keyboard AND focusability); the trusted-event capture
   * guards below are the non-CSS fallback. Escape, the overlay, the
   * ATTIFIT close button and the title/full-details links stay live, and
   * pre-existing inert state is recorded so release restores exactly what
   * existed before. On error everything is restored for a retry; on
   * success the popup closes anyway; after a manual close the stored
   * references point at detached nodes and release just cleans them up.
   */
  // Everything whose value feeds the captured FormData: option pickers,
  // the quantity stepper, and gift-card recipient fields (properties[...]
  // inputs - the same divergence class if a gift card ever renders a
  // popup pill). Symmetry 8.3.2 has no selling-plan selector markup.
  var FREEZE_REGION_SELECTOR = 'variant-picker, quantity-wrapper, gift-card-recipient';

  function freezeRegion(state, el) {
    if (el.hasAttribute('data-attifit-qa-frozen')) return;
    if (!state.frozenEls) state.frozenEls = [];
    el.setAttribute('data-attifit-qa-frozen', el.hasAttribute('inert') ? 'kept' : 'added');
    el.setAttribute('inert', '');
    state.frozenEls.push(el);
  }

  function freezeSubmittedPopupState(state) {
    state.frozenEls = [];
    var active = document.activeElement;
    var relocate = false;
    bodyEl.querySelectorAll(FREEZE_REGION_SELECTOR).forEach(function (el) {
      if (active && el.contains(active)) relocate = true;
      freezeRegion(state, el);
    });
    var pf = state.productForm;
    if (pf && !pf.hasAttribute('aria-busy')) {
      pf.setAttribute('aria-busy', 'true');
      state.busyEl = pf;
    }
    if (relocate) {
      // inert evicts focus (an Enter press inside an option group submits
      // the form): keep it on this popup's own submit button - native has
      // marked it is-loading and aria-disabled, which stays focusable - or
      // the close button.
      var btn = pf && pf.querySelector('[name="add"]');
      (btn || closeBtn).focus({ preventScroll: true });
    }
  }

  function unfreezeSubmittedPopupState(state, outcome) {
    (state.frozenEls || []).forEach(function (el) {
      if (el.getAttribute('data-attifit-qa-frozen') === 'added') el.removeAttribute('inert');
      el.removeAttribute('data-attifit-qa-frozen');
    });
    state.frozenEls = null;
    if (state.busyEl) {
      state.busyEl.removeAttribute('aria-busy');
      state.busyEl = null;
    }
    if (outcome === 'error' && !modal.hidden && state.popupInstance === openToken) {
      // A fragment swapped in during the freeze (only reachable through a
      // programmatic change) arrived force-disabled; hand the button state
      // back to native so the shopper can adjust and retry. A pending
      // variant state may only settle through its own equality check.
      if (variantPending) trySettleVariantState();
      else clearVariantPending();
    }
  }

  /* External drawer return-focus opener. Two separate responsibilities:
   * focusIntoDrawer() manages CURRENT focus while the drawer is open;
   * drawer.opener is where native SideDrawer.close() sends focus when the
   * drawer eventually closes. Native ProductForm updates page-header
   * BEFORE cart-drawer on every successful add, and PageHeader
   * .updateFromCartChange rewrites .logo-area__right via innerHTML - so a
   * drawer opened from the header cart link holds a DETACHED opener after
   * every add (the ATTIFIT re-entry guard in cart-drawer.js rightly skips
   * the redundant re-open, so nothing refreshes it either). A drawer the
   * native flow REopened mid-request received the submitting form's
   * button, detached by the cross-sell merge. Repair rules, applied from
   * settle() - which runs on on:cart:add, dispatched AFTER the native
   * section merges and drawer open:
   *   - a captured opener still connected and outside the drawer is
   *     restored untouched (a PDP add button, any external control);
   *   - a captured opener the merges destroyed (typically the header cart
   *     link) resolves to the freshly rendered connected cart link;
   *   - a mutation that opened the drawer itself returns focus to its
   *     originating card control - if that control is outside the drawer;
   *   - a captured NULL opener stays null across a native reopen;
   *   - nothing inside the drawer is ever assigned (it is about to be
   *     hidden by the very close that would focus it), and null beats any
   *     detached node (close() then simply leaves focus where it is).
   */
  function headerCartLink() {
    var links = document.querySelectorAll('#pageheader .cart-link, page-header .cart-link');
    for (var i = 0; i < links.length; i += 1) {
      if (links[i].isConnected && links[i].getClientRects().length) return links[i];
    }
    return null;
  }

  function validReturnOpener(drawer, el) {
    return !!(el && el.isConnected && !drawer.contains(el) && el.getClientRects().length);
  }

  function resolveDrawerReturnOpener(state) {
    var drawer = document.querySelector('.js-cart-drawer');
    if (!drawer || !drawer.hasAttribute('open')) return; // a future open() sets a fresh opener

    if (state.drawerWasOpen) {
      if (validReturnOpener(drawer, state.drawerReturnOpener)) {
        drawer.opener = state.drawerReturnOpener;
      } else if (state.drawerReturnOpener === null) {
        drawer.opener = null; // deliberately openerless before; keep it so
      } else {
        drawer.opener = headerCartLink();
      }
      return;
    }

    // This mutation opened the drawer (native passed its now-possibly-
    // detached submit button): return focus to the originating card pill.
    drawer.opener = validReturnOpener(drawer, state.trigger) ? state.trigger : headerCartLink();
  }

  function beginQuickAddMutation(opts) {
    quickAddMutationToken += 1;
    var token = quickAddMutationToken;
    var drawerEl = document.querySelector('.js-cart-drawer');
    var drawerWasOpen = !!(drawerEl && drawerEl.hasAttribute('open'));
    var state = {
      token: token,
      mode: opts.mode,
      productForm: opts.productForm,
      productId: opts.productId || '',
      trigger: opts.trigger || null,
      drawerOrigin: opts.drawerOrigin || null,
      popupInstance: opts.popupInstance || 0,
      // External return-focus context, captured before any merge can run:
      // where focus belongs when the DRAWER eventually closes - distinct
      // from the quick-add trigger, which may itself live inside the
      // drawer (see resolveDrawerReturnOpener).
      drawerWasOpen: drawerWasOpen,
      drawerReturnOpener: drawerWasOpen ? (drawerEl.opener || null) : null,
      frozenEls: null,
      busyEl: null
    };
    quickAddMutation = state;
    holdQuickAddControls(opts.productForm);
    if (opts.mode === 'popup') freezeSubmittedPopupState(state);

    var pf = opts.productForm;
    var changeTimer = null;
    var cleanup = function () {
      if (changeTimer) { clearTimeout(changeTimer); changeTimer = null; }
      pf.removeEventListener('on:cart:add', onAdd);
      pf.removeEventListener('on:cart:error', onError);
      pf.removeEventListener('on:cart:change', onChange);
    };
    var settle = function (outcome) {
      cleanup();
      if (quickAddMutation && quickAddMutation.token === token) {
        quickAddMutation = null;
        releaseQuickAddControls();
        if (state.mode === 'popup') unfreezeSubmittedPopupState(state, outcome);
        // on:cart:add is dispatched AFTER the native section merges and
        // drawer open, so this is exactly the right moment to repair the
        // drawer's external return opener the merges may have detached.
        if (outcome !== 'error') resolveDrawerReturnOpener(state);
        if (state.mode === 'popup') finishPopupMutation(state, outcome);
      }
    };
    var onAdd = function () { settle('success'); };
    var onError = function () { settle('error'); };
    var onChange = function () {
      // Native ProductForm dispatches on:cart:change for an unexpected
      // null-section return and then synchronously CONTINUES to
      // drawer.open() and on:cart:add - so on:cart:change is only a
      // deferred fallback: a zero-delay timer that on:cart:add or
      // on:cart:error cancels by settling first.
      if (changeTimer) return;
      changeTimer = setTimeout(function () { settle('change'); }, 0);
    };
    pf.addEventListener('on:cart:add', onAdd);
    pf.addEventListener('on:cart:error', onError);
    pf.addEventListener('on:cart:change', onChange);
  }

  // Authoritative popup completion - runs from the request-specific
  // listener, so it fires whether the popup is still open or was manually
  // closed while the request ran (a detached form cannot bubble to any
  // document listener).
  function finishPopupMutation(state, outcome) {
    if (outcome === 'error') {
      // The popup (if still open) shows the native error in place; the
      // released lock lets the shopper retry. Nothing to reconcile.
      return;
    }

    var drawer = document.querySelector('.js-cart-drawer[open]');
    var popupStillThisInstance = !modal.hidden && state.popupInstance === openToken;

    // The drawer's external return opener was already repaired by
    // resolveDrawerReturnOpener() in settle(); only CURRENT focus remains
    // to be handled here.

    if (popupStillThisInstance) {
      // Close exactly once, origin-aware. Never touches a NEWER popup:
      // the instance check above fails for one.
      if (state.drawerOrigin) {
        closePopup('drawer');
      } else {
        closePopup(drawer ? false : true);
      }
      return;
    }

    // The popup was closed while the request was pending. For drawer
    // origin re-arm the trap onto a valid refreshed element (the merge may
    // have replaced what close focused); for card origin the native open
    // already trapped focus in the drawer.
    if (drawer && state.drawerOrigin) focusIntoDrawer(state.trigger);
  }

  // Direct mini-form submissions (capture phase: the hard serialization
  // gate runs before native ProductForm ever sees a second submit).
  document.addEventListener('submit', function (evt) {
    var form = evt.target;
    if (!form.closest || !form.closest('.attifit-qa-direct')) return;
    if (quickAddMutation) {
      evt.preventDefault();
      evt.stopPropagation();
      return;
    }
    // A submit native ProductForm would reject (aria-disabled while its
    // request runs, disabled while sold out) - or one arriving during its
    // 2s success animation, when aria-disabled is already removed but
    // is-loading remains and native's ONLY resubmit guard is aria-disabled
    // - must neither start a second request nor create a mutation state no
    // completion event would ever settle. Keyboard-generated submits are
    // the reachable path (held CSS only stops pointers). The genuine first
    // submit sees an idle button and passes untouched.
    var submitBtn = form.querySelector('[name="add"]');
    if (submitBtn && (submitBtn.disabled
      || submitBtn.getAttribute('aria-disabled') === 'true'
      || submitBtn.classList.contains('is-loading'))) {
      evt.preventDefault();
      evt.stopPropagation();
      return;
    }
    beginQuickAddMutation({
      mode: 'direct',
      productForm: form.closest('.attifit-qa-direct'),
      productId: form.dataset.productId || '',
      trigger: form.querySelector('[name="add"]'),
      drawerOrigin: form.closest('.js-cart-drawer')
    });
  }, true);

  // Popup submissions cannot establish state in the capture phase: the
  // variant-pending guard may block them, and native validation may reject
  // incomplete options without starting a request (native always calls
  // preventDefault, so that proves nothing). Native ProductForm marks the
  // submit button is-loading synchronously before its first await if - and
  // only if - a request actually began; this bubble listener runs after
  // the native target handler and keys off that.
  modal.addEventListener('submit', function (evt) {
    var form = evt.target;
    if (!form.matches || !form.matches('.js-product-form')) return;
    if (!bodyEl.contains(form)) return;
    if (quickAddMutation) return; // capture guard already blocked new ones; never double-begin
    var productForm = form.closest('product-form') || form.parentElement;
    var btn = productForm.querySelector('[name="add"]');
    if (!btn || !btn.classList.contains('is-loading')) return; // no request started
    beginQuickAddMutation({
      mode: 'popup',
      productForm: productForm,
      productId: form.dataset.productId || '',
      trigger: lastTrigger,
      drawerOrigin: originDrawer,
      popupInstance: openToken
    });
  }, false);

  // Drawer focus recovery for DIRECT adds only - this handler never clears
  // the mutation state (unrelated merges cannot unlock anything) and never
  // competes with popup-success focus handling. It steps in solely when a
  // merge inside the originating drawer actually orphaned focus (which
  // self-limits repeats), including the normal case where the submitting
  // form itself was detached.
  document.addEventListener('on:cart:after-merge', function (evt) {
    if (!quickAddMutation || quickAddMutation.mode !== 'direct') return;
    var state = quickAddMutation;
    var drawer = state.drawerOrigin;
    if (!drawer) return; // PDP origin: the native flow opens/focuses the drawer itself
    if (!drawer.isConnected || !drawer.hasAttribute('open') || !drawer.contains(evt.target)) return;

    var active = document.activeElement;
    var focusLost = !active || active === document.body || !active.isConnected || !drawer.contains(active);
    if (!focusLost) return;
    if (!modal.hidden) return; // a popup owns focus handling right now

    var target = null;
    if (state.productId) {
      target = drawer.querySelector('.attifit-qa-direct .js-product-form[data-product-id="' + state.productId + '"] [name="add"]');
    }
    if (!target) target = drawer.querySelector('.drawer__close-btn') || drawer;
    if (typeof window.trapFocus === 'function') {
      window.trapFocus(drawer, target);
    } else {
      target.focus({ preventScroll: true });
    }
  });

  // Pill clicks (delegated once; survives recommendation AJAX injection,
  // drawer merges and theme-editor section reloads). Pills without
  // data-attifit-qa (link mode) and the direct-add submit buttons are
  // untouched.
  document.addEventListener('click', function (evt) {
    if (!evt.target.closest) return;
    var trigger = evt.target.closest('[data-attifit-qa="popup"]');
    if (!trigger || !trigger.dataset.productUrl) return;
    evt.preventDefault();
    evt.stopPropagation();
    // No new popup while ANY quick-add mutation is in flight (held CSS and
    // aria-disabled are advisory; an anchor still fires clicks - this
    // check is the hard gate).
    if (quickAddMutation) return;
    openPopup(trigger);
  });

  document.addEventListener('pointerover', schedulePrefetch, { passive: true });
  document.addEventListener('pointerout', cancelPrefetch, { passive: true });
})();
